import React, { useState } from 'react';
import { X, Network, Globe, ChevronRight } from 'lucide-react';
import { LocationData } from '../types';
import { HDA_SWITCH_TYPES, CORE_SWITCH_TYPES, TOR_SWITCH_TYPES, SERVER_TYPES } from '../constants';

interface NetworkManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentLocation: string;
  locations: Record<string, LocationData>;
  installHdaSwitch: (locationId: string, floorId: string, switchTypeId: string) => void;
  installCoreSwitch: (locationId: string, switchTypeId: string) => void;
  formatCurrency: (amount: number, decimals?: number) => string;
  money: number;
  onGoToConnectivity?: () => void; // closes this modal and opens the Connectivity (MMR) panel
}

function utilizationColor(pct: number): string {
  if (pct >= 100) return 'text-red-400';
  if (pct >= 80) return 'text-amber-400';
  return 'text-emerald-400';
}

function barColor(pct: number): string {
  if (pct >= 100) return 'bg-red-500';
  if (pct >= 80) return 'bg-amber-500';
  return 'bg-emerald-500';
}

// Compute rack-level traffic and uplink (Gbps)
function getRackBandwidth(rack: any): { trafficGbps: number; uplinkGbps: number } {
  const trafficGbps = (rack.servers ?? []).reduce((sum: number, s: any) => {
    if (s.failureStatus === 'failed') return sum;
    const st = SERVER_TYPES.find(t => t.id === s.type);
    return sum + (st?.trafficGbps ?? 0);
  }, 0);

  if (!rack.torSwitch) return { trafficGbps, uplinkGbps: 0 };
  const tor = TOR_SWITCH_TYPES.find(t => t.id === rack.torSwitch.typeId);
  const uplinkGbps = (tor?.uplinkBandwidthGbps ?? 0) * (rack.torSwitchRedundant ? 2 : 1);
  return { trafficGbps, uplinkGbps };
}

const NetworkManagementModal: React.FC<NetworkManagementModalProps> = ({
  isOpen,
  onClose,
  currentLocation,
  locations,
  installHdaSwitch,
  installCoreSwitch,
  formatCurrency,
  money,
  onGoToConnectivity,
}) => {
  const [selectedFloor, setSelectedFloor] = useState<string | null>(null);

  if (!isOpen) return null;

  const location = locations[currentLocation];
  if (!location) return null;

  const floorEntries = Object.entries(location.floors ?? {});
  const activeFloorId = selectedFloor ?? (floorEntries[0]?.[0] ?? null);
  const activeFloor = activeFloorId ? location.floors[activeFloorId] : null;

  const coreSwitch = location.coreSwitch
    ? CORE_SWITCH_TYPES.find(t => t.id === location.coreSwitch!.typeId)
    : null;

  // Compute per-floor traffic totals for location overview
  const floorTrafficMap: Record<string, { floorTraffic: number; hdaCapacity: number }> = {};
  let locationTotalTraffic = 0;
  Object.entries(location.floors ?? {}).forEach(([fid, floor]) => {
    let floorTraffic = 0;
    (floor.racks ?? []).forEach(rack => {
      const { trafficGbps, uplinkGbps } = getRackBandwidth(rack);
      floorTraffic += Math.min(trafficGbps, uplinkGbps > 0 ? uplinkGbps : trafficGbps);
    });
    const hda = floor.hdaSwitch ? HDA_SWITCH_TYPES.find(t => t.id === floor.hdaSwitch!.typeId) : null;
    floorTrafficMap[fid] = { floorTraffic, hdaCapacity: hda?.bandwidthGbps ?? 0 };
    locationTotalTraffic += Math.min(floorTraffic, hda ? (hda.bandwidthGbps) : floorTraffic);
  });

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-xl border border-gray-700 w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <Network size={18} className="text-blue-400" />
            <h2 className="font-semibold text-white">Data Network — {location.name}</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">

          {/* ── Core Switch ── */}
          <section>
            <h3 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
              Core Switch
              <span className="text-xs font-normal text-gray-500">— racked in the MMR · aggregates all floor HDA uplinks · requires MMR</span>
            </h3>

            {!location.mmr?.built && (
              <div className="mb-3 p-2 rounded bg-amber-900/40 border border-amber-700 text-amber-300 text-xs">
                <strong>MMR not built.</strong> Open the Connectivity panel (Globe icon) and build the Meet-Me Room first — network equipment is racked there.
              </div>
            )}
            {coreSwitch && location.mmr?.built && (location.mmr.transitProviders ?? []).length === 0 && (location.mmr.ixpConnections ?? []).length === 0 && (
              <div className="mb-3 p-2 rounded bg-indigo-900/40 border border-indigo-700 text-indigo-300 text-xs">
                Core switch installed. <strong>Next step:</strong> order a transit provider or connect to an IXP via the Connectivity panel to carry customer traffic.
              </div>
            )}
            {coreSwitch ? (
              <div className="bg-gray-800 rounded-lg p-4 border border-gray-600">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-white">{coreSwitch.name}</span>
                  <span className="text-xs text-gray-400">{coreSwitch.bandwidthGbps} Gbps fabric</span>
                </div>
                <div className="text-xs text-gray-400 mb-3">{coreSwitch.description}</div>
                {/* Location bandwidth bar */}
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-gray-400 w-20 shrink-0">Location</span>
                  <div className="flex-1 bg-gray-700 rounded-full h-2 overflow-hidden">
                    <div
                      className={`h-2 rounded-full transition-all ${barColor(locationTotalTraffic / coreSwitch.bandwidthGbps * 100)}`}
                      style={{ width: `${Math.min(100, locationTotalTraffic / coreSwitch.bandwidthGbps * 100).toFixed(1)}%` }}
                    />
                  </div>
                  <span className={`w-24 text-right font-mono ${utilizationColor(locationTotalTraffic / coreSwitch.bandwidthGbps * 100)}`}>
                    {locationTotalTraffic.toFixed(1)} / {coreSwitch.bandwidthGbps} G
                  </span>
                </div>
                {/* Upgrade options if higher tier available */}
                {CORE_SWITCH_TYPES.filter(t => t.bandwidthGbps > coreSwitch.bandwidthGbps).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-700">
                    <p className="text-xs text-gray-500 mb-2">Upgrade:</p>
                    <div className="flex flex-wrap gap-2">
                      {CORE_SWITCH_TYPES.filter(t => t.bandwidthGbps > coreSwitch.bandwidthGbps).map(t => (
                        <button
                          key={t.id}
                          onClick={() => installCoreSwitch(currentLocation, t.id)}
                          disabled={money < t.cost}
                          className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${money >= t.cost ? 'bg-blue-900 border-blue-600 text-blue-200 hover:bg-blue-800' : 'bg-gray-800 border-gray-600 text-gray-500 cursor-not-allowed'}`}
                        >
                          {t.name} — {formatCurrency(t.cost)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-gray-800 rounded-lg p-4 border border-dashed border-gray-600">
                <p className="text-sm text-gray-400 mb-3">No core switch installed. The core switch lives in the MMR and aggregates all floor HDA uplinks. <strong className="text-gray-300">Build the MMR first</strong> (Connectivity panel) — the core switch is racked there and connects your floors to transit providers.</p>
                <div className="space-y-2">
                  {CORE_SWITCH_TYPES.map(t => (
                    <div key={t.id} className="flex items-center justify-between bg-gray-900 rounded-lg p-3 border border-gray-700">
                      <div>
                        <div className="text-sm font-medium text-white">{t.name}</div>
                        <div className="text-xs text-gray-400">{t.bandwidthGbps} Gbps · {t.powerDrawKW} kW · {t.description.split('.')[0]}</div>
                      </div>
                      <button
                        onClick={() => installCoreSwitch(currentLocation, t.id)}
                        disabled={money < t.cost}
                        className={`ml-4 text-xs px-3 py-1.5 rounded-lg border shrink-0 transition-colors ${money >= t.cost ? 'bg-blue-900 border-blue-600 text-blue-200 hover:bg-blue-800' : 'bg-gray-800 border-gray-600 text-gray-500 cursor-not-allowed'}`}
                      >
                        {formatCurrency(t.cost)}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* ── Floor HDA Switch ── */}
          <section>
            <h3 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
              Floor Aggregation (HDA)
              <span className="text-xs font-normal text-gray-500">— one per floor · aggregates TOR uplinks · connects back to Core Switch in MMR</span>
            </h3>

            {/* Floor tabs */}
            {floorEntries.length > 1 && (
              <div className="flex gap-1 mb-3">
                {floorEntries.map(([fid, fl]) => (
                  <button
                    key={fid}
                    onClick={() => setSelectedFloor(fid)}
                    className={`text-xs px-3 py-1 rounded-md border transition-colors ${fid === activeFloorId ? 'bg-blue-900 border-blue-600 text-blue-200' : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-gray-200'}`}
                  >
                    {fl.name}
                  </button>
                ))}
              </div>
            )}

            {activeFloor ? (() => {
              const hda = activeFloor.hdaSwitch
                ? HDA_SWITCH_TYPES.find(t => t.id === activeFloor.hdaSwitch!.typeId)
                : null;

              // Per-rack bandwidth
              const rackRows = (activeFloor.racks ?? []).filter(r => r.type !== null);

              let floorTotalTraffic = 0;
              rackRows.forEach(rack => {
                const { trafficGbps, uplinkGbps } = getRackBandwidth(rack);
                floorTotalTraffic += Math.min(trafficGbps, uplinkGbps > 0 ? uplinkGbps : trafficGbps);
              });

              return (
                <div className="space-y-3">
                  {/* HDA install/status */}
                  {hda ? (
                    <div className="bg-gray-800 rounded-lg p-4 border border-gray-600">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-white">{hda.name}</span>
                        <span className="text-xs text-gray-400">{hda.bandwidthGbps} Gbps</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs mb-1">
                        <span className="text-gray-400 w-20 shrink-0">Floor total</span>
                        <div className="flex-1 bg-gray-700 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-2 rounded-full transition-all ${barColor(floorTotalTraffic / hda.bandwidthGbps * 100)}`}
                            style={{ width: `${Math.min(100, floorTotalTraffic / hda.bandwidthGbps * 100).toFixed(1)}%` }}
                          />
                        </div>
                        <span className={`w-24 text-right font-mono ${utilizationColor(floorTotalTraffic / hda.bandwidthGbps * 100)}`}>
                          {floorTotalTraffic.toFixed(1)} / {hda.bandwidthGbps} G
                        </span>
                      </div>
                      {HDA_SWITCH_TYPES.filter(t => t.bandwidthGbps > hda.bandwidthGbps).length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-700">
                          <p className="text-xs text-gray-500 mb-2">Upgrade:</p>
                          <div className="flex flex-wrap gap-2">
                            {HDA_SWITCH_TYPES.filter(t => t.bandwidthGbps > hda.bandwidthGbps).map(t => (
                              <button
                                key={t.id}
                                onClick={() => installHdaSwitch(currentLocation, activeFloorId!, t.id)}
                                disabled={money < t.cost}
                                className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${money >= t.cost ? 'bg-blue-900 border-blue-600 text-blue-200 hover:bg-blue-800' : 'bg-gray-800 border-gray-600 text-gray-500 cursor-not-allowed'}`}
                              >
                                {t.name} — {formatCurrency(t.cost)}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-gray-800 rounded-lg p-4 border border-dashed border-gray-600">
                      <p className="text-sm text-gray-400 mb-3">No HDA switch on {activeFloor.name}. Without one, floor bandwidth is not aggregated — high-traffic floors will incur a revenue penalty.</p>
                      <div className="space-y-2">
                        {HDA_SWITCH_TYPES.map(t => (
                          <div key={t.id} className="flex items-center justify-between bg-gray-900 rounded-lg p-3 border border-gray-700">
                            <div>
                              <div className="text-sm font-medium text-white">{t.name}</div>
                              <div className="text-xs text-gray-400">{t.bandwidthGbps} Gbps · {t.powerDrawKW} kW · {t.description.split('.')[0]}</div>
                            </div>
                            <button
                              onClick={() => installHdaSwitch(currentLocation, activeFloorId!, t.id)}
                              disabled={money < t.cost}
                              className={`ml-4 text-xs px-3 py-1.5 rounded-lg border shrink-0 transition-colors ${money >= t.cost ? 'bg-blue-900 border-blue-600 text-blue-200 hover:bg-blue-800' : 'bg-gray-800 border-gray-600 text-gray-500 cursor-not-allowed'}`}
                            >
                              {formatCurrency(t.cost)}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Per-rack bandwidth table */}
                  {rackRows.length > 0 && (
                    <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-gray-700 text-gray-400">
                            <th className="text-left px-3 py-2">Rack</th>
                            <th className="text-left px-3 py-2">TOR</th>
                            <th className="text-right px-3 py-2">Traffic</th>
                            <th className="text-right px-3 py-2">Uplink</th>
                            <th className="px-3 py-2 w-20">Util%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rackRows.map((rack, i) => {
                            const { trafficGbps, uplinkGbps } = getRackBandwidth(rack);
                            const tor = rack.torSwitch
                              ? TOR_SWITCH_TYPES.find(t => t.id === (rack.torSwitch as any).typeId)
                              : null;
                            const pct = uplinkGbps > 0 ? trafficGbps / uplinkGbps * 100 : (trafficGbps > 0 ? 999 : 0);
                            return (
                              <tr key={rack.id} className="border-b border-gray-700/50 last:border-0">
                                <td className="px-3 py-1.5 text-gray-300">#{i + 1}</td>
                                <td className="px-3 py-1.5 text-gray-400">
                                  {tor ? (
                                    <span>{tor.uplinkBandwidthGbps}G{rack.torSwitchRedundant ? ' ×2' : ''}</span>
                                  ) : (
                                    <span className="text-gray-600">—</span>
                                  )}
                                </td>
                                <td className="px-3 py-1.5 text-right font-mono text-gray-300">
                                  {trafficGbps > 0 ? `${trafficGbps.toFixed(1)}G` : '—'}
                                </td>
                                <td className="px-3 py-1.5 text-right font-mono text-gray-300">
                                  {uplinkGbps > 0 ? `${uplinkGbps}G` : '—'}
                                </td>
                                <td className="px-3 py-1.5 text-right">
                                  {uplinkGbps > 0 || trafficGbps > 0 ? (
                                    <span className={`font-mono font-semibold ${utilizationColor(pct)}`}>
                                      {pct >= 999 ? 'NO TOR' : `${pct.toFixed(0)}%`}
                                    </span>
                                  ) : <span className="text-gray-600">—</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {rackRows.length === 0 && (
                    <p className="text-xs text-gray-500">No racks installed on this floor yet.</p>
                  )}
                </div>
              );
            })() : (
              <p className="text-xs text-gray-500">No floors available.</p>
            )}
          </section>

          {/* ── Location Bandwidth Summary ── */}
          {floorEntries.length > 1 && (
            <section>
              <h3 className="text-sm font-semibold text-gray-300 mb-3">Location Bandwidth Summary</h3>
              <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-700 text-gray-400">
                      <th className="text-left px-3 py-2">Floor</th>
                      <th className="text-right px-3 py-2">Traffic</th>
                      <th className="text-right px-3 py-2">HDA Cap</th>
                      <th className="px-3 py-2 w-20">Util%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {floorEntries.map(([fid, fl]) => {
                      const { floorTraffic, hdaCapacity } = floorTrafficMap[fid] ?? { floorTraffic: 0, hdaCapacity: 0 };
                      const pct = hdaCapacity > 0 ? floorTraffic / hdaCapacity * 100 : (floorTraffic > 0 ? 999 : 0);
                      return (
                        <tr key={fid} className="border-b border-gray-700/50 last:border-0">
                          <td className="px-3 py-1.5 text-gray-300">{fl.name}</td>
                          <td className="px-3 py-1.5 text-right font-mono text-gray-300">{floorTraffic.toFixed(1)} G</td>
                          <td className="px-3 py-1.5 text-right font-mono text-gray-400">
                            {hdaCapacity > 0 ? `${hdaCapacity} G` : <span className="text-gray-600">No HDA</span>}
                          </td>
                          <td className="px-3 py-1.5 text-right">
                            {pct >= 999 ? (
                              <span className="font-mono font-semibold text-amber-400">No HDA</span>
                            ) : pct > 0 ? (
                              <span className={`font-mono font-semibold ${utilizationColor(pct)}`}>{pct.toFixed(0)}%</span>
                            ) : (
                              <span className="text-gray-600">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="border-t border-gray-600 bg-gray-900/50">
                      <td className="px-3 py-1.5 font-semibold text-gray-200">Total</td>
                      <td className="px-3 py-1.5 text-right font-mono font-semibold text-gray-200">{locationTotalTraffic.toFixed(1)} G</td>
                      <td className="px-3 py-1.5 text-right font-mono text-gray-400">
                        {coreSwitch ? `${coreSwitch.bandwidthGbps} G` : <span className="text-gray-600">No Core</span>}
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        {coreSwitch && locationTotalTraffic > 0 ? (
                          <span className={`font-mono font-semibold ${utilizationColor(locationTotalTraffic / coreSwitch.bandwidthGbps * 100)}`}>
                            {(locationTotalTraffic / coreSwitch.bandwidthGbps * 100).toFixed(0)}%
                          </span>
                        ) : <span className="text-gray-600">—</span>}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>

        {/* Next-step footer — chains the setup flow toward connectivity (MMR) */}
        {onGoToConnectivity && (
          <div className="border-t border-gray-700 p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="text-sm">
              {location.coreSwitch ? (
                <span className="text-emerald-400 font-medium">✓ Core switch installed — connect to the internet next.</span>
              ) : (
                <span className="text-gray-400">Install a core switch above, then order connectivity so your servers can carry traffic.</span>
              )}
            </div>
            <button
              onClick={onGoToConnectivity}
              className={`shrink-0 px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5 transition-colors ${
                location.coreSwitch
                  ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-900/30'
                  : 'bg-gray-700 hover:bg-gray-600 text-gray-200'
              }`}
            >
              <Globe className="w-4 h-4" />
              Order connectivity
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NetworkManagementModal;
